/**
 * Work Log Review filter strip — task 159c §D7.
 *
 * Five filters: classificationSource (multi), employees (multi), business
 * unit (single), client (single), project (single). Project options are
 * narrowed to the currently picked client. The page owns the URL state;
 * this component is a pure controlled view.
 *
 * Date range is intentionally absent — DESIGN-RULES Rule 35: pages
 * consume `dateRange` from `AppContext`, the global TopBar is the only
 * date input.
 */

import { useId, useMemo } from 'react';
import { Select } from '@/components/primitives/Select';
import { Checkbox } from '@/components/primitives/Checkbox';
import { useEmployees } from '@/hooks/useEmployees';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import type { ClassificationSource } from '@/types/workLogReview';
import styles from './WorkLogReviewFilters.module.css';

const ALL_SOURCES: readonly ClassificationSource[] = ['fallback', 'rule', 'manual', 'ai'];
// Audience-first labels — admin users read these; the engineer term
// "fallback" lives only in the wire contract. The page subtitle already
// frames the workflow around "default category", so the filter label and
// the row badge in WorkLogReviewEventLog stay in sync with that wording.
const SOURCE_LABELS: Record<ClassificationSource, string> = {
  fallback: 'Default category',
  rule: 'Matched a rule',
  manual: 'Set manually',
  ai: 'AI-suggested',
};

export interface WorkLogReviewFiltersValue {
  classificationSource: readonly ClassificationSource[];
  employeeIds: readonly string[];
  businessUnitId: string;
  clientId: string;
  projectId: string;
}

interface WorkLogReviewFiltersProps {
  value: WorkLogReviewFiltersValue;
  onChange: (next: WorkLogReviewFiltersValue) => void;
}

export function WorkLogReviewFilters({ value, onChange }: WorkLogReviewFiltersProps) {
  const { data: employees } = useEmployees();
  const { data: clients } = useClients();
  const { data: projects } = useProjects();
  const { data: businessUnits } = useBusinessUnits();

  const sourceGroupId = useId();
  const employeeGroupId = useId();

  const filteredProjects = useMemo(
    () => (value.clientId ? projects.filter((p) => p.clientId === value.clientId) : projects),
    [projects, value.clientId],
  );

  const toggleSource = (s: ClassificationSource) => {
    const set = new Set(value.classificationSource);
    if (set.has(s)) set.delete(s);
    else set.add(s);
    onChange({ ...value, classificationSource: ALL_SOURCES.filter((x) => set.has(x)) });
  };

  const toggleEmployee = (id: string) => {
    const set = new Set(value.employeeIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange({ ...value, employeeIds: [...set] });
  };

  const onPickClient = (next: string) => {
    // If the picked project belongs to a different client, drop it.
    let nextProjectId = value.projectId;
    if (next && value.projectId) {
      const proj = projects.find((p) => p.id === value.projectId);
      if (proj && proj.clientId !== next) nextProjectId = '';
    } else if (!next) {
      nextProjectId = '';
    }
    onChange({ ...value, clientId: next, projectId: nextProjectId });
  };

  return (
    <div className={styles.wrapper} data-testid="wlr-filters">
      <fieldset className={styles.group} aria-labelledby={sourceGroupId}>
        <legend id={sourceGroupId} className={styles.legend}>
          Classification source
        </legend>
        <div className={styles.checkboxRow}>
          {ALL_SOURCES.map((s) => (
            <Checkbox
              key={s}
              checked={value.classificationSource.includes(s)}
              onChange={() => toggleSource(s)}
              label={SOURCE_LABELS[s]}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.group} aria-labelledby={employeeGroupId}>
        <legend id={employeeGroupId} className={styles.legend}>
          Employees
        </legend>
        <div className={styles.checkboxRow}>
          {employees.length === 0 ? (
            <span className={styles.empty}>No employees loaded.</span>
          ) : (
            employees.map((e) => (
              <Checkbox
                key={e.id}
                checked={value.employeeIds.includes(e.id)}
                onChange={() => toggleEmployee(e.id)}
                label={e.name}
              />
            ))
          )}
        </div>
      </fieldset>

      <div className={styles.selectsRow}>
        <Select
          value={value.businessUnitId}
          options={[
            { value: '', label: 'All business units' },
            ...businessUnits.map((b) => ({ value: b.id, label: b.name })),
          ]}
          onChange={(v) => onChange({ ...value, businessUnitId: v })}
          label="Business unit"
          size="sm"
        />
        <Select
          value={value.clientId}
          options={[
            { value: '', label: 'All clients' },
            ...clients.map((c) => ({ value: c.id, label: c.name })),
          ]}
          onChange={onPickClient}
          label="Client"
          size="sm"
        />
        <Select
          value={value.projectId}
          options={[
            { value: '', label: 'All projects' },
            ...filteredProjects.map((p) => ({ value: p.id, label: p.name })),
          ]}
          onChange={(v) => onChange({ ...value, projectId: v })}
          label="Project"
          size="sm"
        />
      </div>
    </div>
  );
}
